/**
 * Third-party reachability probe.
 *
 * Answers a question the player cannot answer on its own: when a request hangs, is it the device
 * that lost the internet, or is it our backend that stopped answering? `navigator.onLine` only
 * reports a link-layer connection (it stays `true` on a dead Wi-Fi), and probing our own API would
 * conflate both cases, so the probe deliberately targets a neutral third-party host.
 *
 * The endpoint sends no CORS headers, so the request has to be `no-cors` and the response is opaque:
 * neither status nor body can be read. That is enough here — the probe only distinguishes "something
 * on the other end answered in time" from "nothing answered" — but it does mean a captive portal that
 * intercepts the request looks like a working connection (detecting that needs Apple's `Success` body,
 * i.e. a CORS-enabled endpoint of our own).
 */

/** Apple's captive-portal detector: tiny, unauthenticated, no CORS, highly available. */
export const PROBE_URL = 'https://captive.apple.com/hotspot-detect.html';

/**
 * - `online` — the host answered within the timeout, so the device really has a connection.
 * - `unreachable` — nothing answered in time (timeout, DNS failure, blocked host).
 * - `aborted` — the caller cancelled the probe; the result says nothing about the network and must be ignored.
 */
export type TConnectivityVerdict = 'online' | 'unreachable' | 'aborted';

/**
 * Checks whether the device can reach the outside world.
 *
 * @param timeoutMs how long to wait for an answer, defaults to 500 ms
 * @param externalSignal cancels the probe when the caller is no longer interested (e.g. the request it
 * was launched for has settled); a probe cancelled this way resolves to `aborted`, never to a verdict.
 */
export const probeConnectivity = async (timeoutMs = 500, externalSignal?: AbortSignal): Promise<TConnectivityVerdict> => {
    if (externalSignal?.aborted) {
        return 'aborted';
    }

    // Own controller so the timeout and the caller can both cancel the same fetch.
    // `AbortSignal.timeout` / `AbortSignal.any` are skipped on purpose — Safari support starts at 16 / 17.4.
    const controller = new AbortController();
    const abort = () => controller.abort();
    externalSignal?.addEventListener('abort', abort);
    const timeoutId = setTimeout(abort, timeoutMs);

    try {
        // `no-cors` because the host sends no `Access-Control-Allow-Origin`; `no-store` so a cached
        // answer can never stand in for a real round trip. Referrer is stripped: the host has no
        // business knowing which page of ours is asking.
        await fetch(PROBE_URL, {
            method: 'GET',
            mode: 'no-cors',
            cache: 'no-store',
            credentials: 'omit',
            referrerPolicy: 'no-referrer',
            signal: controller.signal,
        });
        return 'online';
    } catch (e) {
        console.error(e);
        // Both a real network failure and our own abort land here — only the caller's abort is special.
        return externalSignal?.aborted ? 'aborted' : 'unreachable';
    } finally {
        clearTimeout(timeoutId);
        externalSignal?.removeEventListener('abort', abort);
    }
};
